import { ErrorOutline, Visibility, VisibilityOff } from '@mui/icons-material';
import {
  Alert,
  Box,
  Button,
  IconButton,
  InputAdornment,
  Link,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';
import React, { useState } from 'react';
import { useStudioUnauthenticatedContext } from '../../context/StudioContext';
import { AuthCard } from '../Auth/AuthCard';
import './SignupForm.css';

interface SignupFormProps {
  firstName: string;
  email?: string | null;
  onSignInClick: () => void;
  token?: string | null;
}

export const SignupForm: React.FC<SignupFormProps> = ({
  firstName,
  email,
  onSignInClick,
  token,
}) => {
  const theme = useTheme();
  const { login, authenticateWithToken, currentUser, backendClient } =
    useStudioUnauthenticatedContext();
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const handleTogglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const handleToggleConfirmPasswordVisibility = () => {
    setShowConfirmPassword(!showConfirmPassword);
  };

  const validatePassword = (password: string): boolean => {
    // Password must be at least 8 characters and contain a number and special character
    const hasMinLength = password.length >= 8;
    const hasNumber = /\d/.test(password);
    const hasSpecialChar = /[!@#$%^&*(),.?":{}|<>]/.test(password);

    return hasMinLength && hasNumber && hasSpecialChar;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // If we have a token, use token-based authentication
    if (token) {
      setIsLoading(true);
      setError(null);

      try {
        // Validate password requirements for token-based signup
        if (!validatePassword(password)) {
          setError(
            'Password must be at least 8 characters and contain a number and special character.',
          );
          return;
        }

        // Validate password confirmation
        if (password !== confirmPassword) {
          setError('Passwords do not match.');
          return;
        }

        // Call the reset-password endpoint
        const resetResponse = await backendClient.resetPassword(
          token,
          password,
        );

        // After successful password reset, authenticate with the returned token and user data
        await authenticateWithToken(resetResponse.token, resetResponse.user);

        // The authentication will trigger the redirect to dashboard
        // via the useEffect in SignupPage that watches currentUser
      } catch (err: any) {
        console.error('Password reset failed:', err);

        // Handle specific error cases
        if (
          err.message?.includes('401') ||
          err.message?.includes('Invalid or expired')
        ) {
          setError('Invalid or expired token. Please contact support.');
        } else if (
          err.message?.includes('400') ||
          err.message?.includes('Validation')
        ) {
          setError('Password must be at least 8 characters long.');
        } else {
          setError('Failed to complete account setup. Please try again.');
        }
      } finally {
        setIsLoading(false);
      }
      return;
    }

    // Fallback to password-based authentication
    // Validate password requirements
    if (!validatePassword(password)) {
      setError(
        'Password must be at least 8 characters and contain a number and special character.',
      );
      return;
    }

    // Validate password confirmation
    if (password !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      // Use hardcoded email for demo purposes (in real app, collect email separately)
      // TODO: implement login with email and password
      await login('test@feed.fm', password);
    } catch (err) {
      setError('Failed to create account. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  // If we have a token, show a simplified form
  if (token) {
    return (
      <Box className="signup-form-container">
        <AuthCard
          title={`Welcome to Clips Studio${firstName ? `, ${firstName}` : ''}.`}
        >
          <Box sx={{ mb: 3 }}>
            <Typography variant="body1" className="signup-form-description">
              Complete your account setup with a secure password.
            </Typography>
          </Box>

          <form onSubmit={handleSubmit}>
            <Box className="signup-form-form-container">
              {/* Username Field */}
              {email && (
                <TextField
                  fullWidth
                  label="Username"
                  value={email}
                  variant="outlined"
                  disabled
                  className="signup-form-textfield"
                />
              )}

              {/* Password Field */}
              <TextField
                fullWidth
                label="Password"
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => {
                  setPassword(e.target.value);
                  setError(null); // Clear error when user starts typing
                }}
                variant="outlined"
                className="signup-form-textfield"
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="toggle password visibility"
                        onClick={handleTogglePasswordVisibility}
                        edge="end"
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />

              {/* Re-enter Password Field */}
              <TextField
                fullWidth
                label="Re-enter Password"
                type={showConfirmPassword ? 'text' : 'password'}
                value={confirmPassword}
                onChange={(e) => {
                  setConfirmPassword(e.target.value);
                  setError(null); // Clear error when user starts typing
                }}
                variant="outlined"
                className="signup-form-textfield"
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="toggle password visibility"
                        onClick={handleToggleConfirmPasswordVisibility}
                        edge="end"
                      >
                        {showConfirmPassword ? (
                          <VisibilityOff />
                        ) : (
                          <Visibility />
                        )}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />

              {/* Password Requirements */}
              <Typography
                variant="body2"
                className="signup-form-password-requirements"
              >
                Passwords need to be at least 8 characters and contain a number
                and special character.
              </Typography>

              <Button
                type="submit"
                fullWidth
                variant="contained"
                disabled={isLoading}
                className="signup-form-create-account-button"
              >
                {isLoading ? 'Creating account...' : 'Create account'}
              </Button>

              <Box className="signup-form-signin-row">
                <Typography
                  variant="body1"
                  className="signup-form-already-member-text"
                >
                  Already a member?
                </Typography>
                <Link
                  component="button"
                  type="button"
                  onClick={onSignInClick}
                  underline="always"
                  className="signup-form-signin-link"
                >
                  Sign in
                </Link>
              </Box>
            </Box>
          </form>
        </AuthCard>

        {/* Error Alert */}
        {error && (
          <Alert
            severity="error"
            icon={<ErrorOutline />}
            className="signup-form-error-alert"
          >
            <Typography variant="h6" className="signup-form-error-title">
              Setup failed
            </Typography>
            <Typography variant="body2" className="signup-form-error-message">
              {error}
            </Typography>
          </Alert>
        )}
      </Box>
    );
  }

  return (
    <Box className="signup-form-container">
      <AuthCard
        title={`Welcome to Clips Studio${firstName ? `, ${firstName}` : ''}.`}
      >
        <Box sx={{ mb: 3 }}>
          <Typography variant="body1" className="signup-form-description">
            Get started with your Feed Clips account!
          </Typography>
        </Box>

        <form onSubmit={handleSubmit}>
          <Box className="signup-form-form-container">
            {/* Username Field */}
            {email && (
              <TextField
                fullWidth
                label="Username"
                value={email}
                variant="outlined"
                disabled
                className="signup-form-textfield"
              />
            )}

            {/* Password Field */}
            <TextField
              fullWidth
              label="Password"
              type={showPassword ? 'text' : 'password'}
              value={password}
              onChange={(e) => {
                setPassword(e.target.value);
                setError(null); // Clear error when user starts typing
              }}
              variant="outlined"
              className="signup-form-textfield"
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={handleTogglePasswordVisibility}
                      edge="end"
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            {/* Re-enter Password Field */}
            <TextField
              fullWidth
              label="Re-enter Password"
              type={showConfirmPassword ? 'text' : 'password'}
              value={confirmPassword}
              onChange={(e) => {
                setConfirmPassword(e.target.value);
                setError(null); // Clear error when user starts typing
              }}
              variant="outlined"
              className="signup-form-textfield"
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={handleToggleConfirmPasswordVisibility}
                      edge="end"
                    >
                      {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            {/* Password Requirements */}
            <Typography
              variant="body2"
              className="signup-form-password-requirements"
            >
              Passwords need to be at least 8 characters and contain a number
              and special character.
            </Typography>

            {/* Create Account Button */}
            <Button
              type="submit"
              fullWidth
              variant="contained"
              disabled={isLoading}
              className="signup-form-create-account-button"
            >
              {isLoading ? 'Creating account...' : 'Create account'}
            </Button>

            {/* Sign in Link Row */}
            <Box className="signup-form-signin-row">
              <Typography
                variant="body1"
                className="signup-form-already-member-text"
              >
                Already a member?
              </Typography>
              <Link
                component="button"
                type="button"
                onClick={onSignInClick}
                underline="always"
                className="signup-form-signin-link"
              >
                Sign in
              </Link>
            </Box>
          </Box>
        </form>
      </AuthCard>

      {/* Error Alert */}
      {error && (
        <Alert
          severity="error"
          icon={<ErrorOutline />}
          className="signup-form-error-alert"
        >
          <Typography variant="h6" className="signup-form-error-title">
            Account creation failed
          </Typography>
          <Typography variant="body2" className="signup-form-error-message">
            {error}
          </Typography>
        </Alert>
      )}
    </Box>
  );
};
